/**
 * Гибридные логические часы.
 *
 * Строка `<миллисекунды>-<счётчик>-<устройство>` в base36 с фиксированной
 * шириной первых двух частей: сравнение строк совпадает со сравнением времени,
 * поэтому часы годятся и как ключ сортировки, и как значение в базе.
 *
 * Свойства, ради которых берутся именно они (раздел 3.2 архитектуры):
 * часы монотонны внутри устройства даже при переводе системного времени назад;
 * при получении чужой мутации локальные часы догоняют её, поэтому ответ на
 * правку всегда «позже» этой правки; хвост с идентификатором устройства делает
 * порядок полным.
 */
const MS_WIDTH = 11;
const COUNTER_WIDTH = 4;
const MAX_COUNTER = 36 ** COUNTER_WIDTH - 1;
export function encodeHlc(parts) {
    if (!Number.isInteger(parts.ms) || parts.ms < 0) {
        throw new Error('HLC: миллисекунды должны быть неотрицательным целым');
    }
    if (parts.counter < 0 || parts.counter > MAX_COUNTER) {
        throw new Error('HLC: счётчик вышел за разрядность');
    }
    if (parts.deviceId.length === 0) {
        throw new Error('HLC: устройство не задано');
    }
    const ms = parts.ms.toString(36).padStart(MS_WIDTH, '0');
    const counter = parts.counter.toString(36).padStart(COUNTER_WIDTH, '0');
    return `${ms}-${counter}-${parts.deviceId}`;
}
export function decodeHlc(hlc) {
    const first = hlc.indexOf('-');
    const second = hlc.indexOf('-', first + 1);
    if (first !== MS_WIDTH || second !== MS_WIDTH + 1 + COUNTER_WIDTH) {
        throw new Error(`HLC: неразбираемое значение ${hlc}`);
    }
    return {
        ms: Number.parseInt(hlc.slice(0, first), 36),
        counter: Number.parseInt(hlc.slice(first + 1, second), 36),
        deviceId: hlc.slice(second + 1),
    };
}
/** Сравнение строк, а не разбор: ради этого часы и кодируются фиксированной шириной. */
export function compareHlc(a, b) {
    if (a === b)
        return 0;
    return a < b ? -1 : 1;
}
export function maxHlc(a, b) {
    if (a === undefined)
        return b;
    if (b === undefined)
        return a;
    return compareHlc(a, b) >= 0 ? a : b;
}
export function createClock(options) {
    const { deviceId } = options;
    const wallClock = options.wallClock ?? (() => Date.now());
    let ms = options.restore?.ms ?? 0;
    let counter = options.restore?.counter ?? 0;
    function bump(remote) {
        const wall = wallClock();
        const previousMs = ms;
        const next = Math.max(wall, previousMs, remote?.ms ?? 0);
        if (next === previousMs && remote !== undefined && next === remote.ms) {
            counter = Math.max(counter, remote.counter) + 1;
        }
        else if (next === previousMs) {
            counter += 1;
        }
        else if (remote !== undefined && next === remote.ms) {
            counter = remote.counter + 1;
        }
        else {
            counter = 0;
        }
        ms = next;
        return encodeHlc({ ms, counter, deviceId });
    }
    return {
        now: () => bump(),
        receive: (remote) => bump(decodeHlc(remote)),
        state: () => ({ ms, counter, deviceId }),
    };
}
//# sourceMappingURL=hlc.js.map